// Sentry initialization — must be the first import in the app entry.
// No-op when REACT_APP_SENTRY_DSN is not set (e.g. local dev without monitoring).
//
// Setup:
//   1. Sign up at https://sentry.io → Create project (Platform: React)
//   2. Copy the DSN from Settings → Client Keys (DSN)
//   3. Set REACT_APP_SENTRY_DSN in /app/frontend/.env
//   4. Restart the dev server / rebuild for production
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
    release: process.env.REACT_APP_SENTRY_RELEASE || "ai-growth-operator@dev",
    sampleRate: 1.0,
    tracePropagationTargets: [], // tracing disabled — stays in free tier
    integrations: [],
    // Explicitly disable session replay — even when not added to integrations,
    // some Sentry SDK builds auto-load rrweb if available and that crashes
    // intermittently with "Cannot read properties of undefined (reading
    // 'bufferBelongsToIframe')" inside cross-origin iframes. Set to 0 + below
    // ignoreErrors filter to silence both the feature and the error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    maxBreadcrumbs: 50,
    ignoreErrors: [
      // Browser extensions and bookmarklets
      "top.GLOBALS",
      "chrome-extension://",
      "moz-extension://",
      // Generic transient network errors
      "NetworkError: Failed to fetch",
      "NetworkError: Network request failed",
      // Webpack code-splitting transient errors
      /Loading chunk \d+ failed/,
      // Ads / tracking scripts noise
      "adsbygoogle",
      "gtag",
      // Sentry's own session-replay rrweb library noise — not actionable
      /bufferBelongsToIframe/,
      /rrweb-record/,
    ],
    beforeSend(event, hint) {
      // Drop generic cross-origin "Script error." noise
      const exc = hint?.originalException;
      if (exc?.message === "Script error.") return null;
      // Drop any error originating in rrweb / session-replay frames
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
      if (frames.some((f) => (f.filename || "").includes("rrweb"))) return null;
      const msg = (exc?.message || event?.message || "").toLowerCase();
      if (msg.includes("bufferbelongstoiframe")) return null;
      return event;
    },
  });
  // eslint-disable-next-line no-console
  console.info("[Sentry] Frontend monitoring enabled");
}

export default Sentry;
